// Create / edit modal for a SubdomainMonitor.

import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { subdomainMonitorsAPI } from '../../services/api';
import SubdomainToolPicker from './SubdomainToolPicker';

const SCHEDULE_OPTIONS = [
  { value: 'hourly', label: 'Every hour' },
  { value: 'every_6h', label: 'Every 6 hours' },
  { value: 'daily', label: 'Daily (03:00 UTC)' },
  { value: 'weekly', label: 'Weekly (Mon 03:00 UTC)' },
];

const DEFAULT_TOOLS = ['subfinder', 'shodan'];

export default function SubdomainMonitorModal({ isOpen, onClose, onSaved, target }) {
  const isEdit = !!target;
  const [label, setLabel] = useState('');
  const [rootDomain, setRootDomain] = useState('');
  const [scheduleKind, setScheduleKind] = useState('daily');
  const [tools, setTools] = useState(DEFAULT_TOOLS);
  const [wordlistSize, setWordlistSize] = useState('top_5k');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setError(null);
      if (target) {
        setLabel(target.label || '');
        setRootDomain(target.root_domain || '');
        setScheduleKind(target.schedule_kind || 'daily');
        setTools(Array.isArray(target.tools) && target.tools.length ? target.tools : DEFAULT_TOOLS);
        setWordlistSize(target.wordlist_size || 'top_5k');
      } else {
        setLabel('');
        setRootDomain('');
        setScheduleKind('daily');
        setTools(DEFAULT_TOOLS);
        setWordlistSize('top_5k');
      }
    }
  }, [isOpen, target]);


  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (tools.length === 0) {
      setError('Select at least one tool.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      if (isEdit) {
        await subdomainMonitorsAPI.update(target.id, {
          label: label.trim() || null,
          schedule_kind: scheduleKind,
          tools,
          wordlist_size: wordlistSize,
        });
      } else {
        await subdomainMonitorsAPI.create({
          label: label.trim() || null,
          root_domain: rootDomain.trim(),
          schedule_kind: scheduleKind,
          tools,
          wordlist_size: wordlistSize,
        });
      }
      onSaved && onSaved();
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="w-full max-w-md rounded-2xl border border-cyber-300/40 bg-cyber-50 shadow-2xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-cyber-300/30">
          <h2 className="text-lg font-semibold text-slate-900">
            {isEdit ? 'Edit subdomain monitor' : 'New subdomain monitor'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="text-slate-500 hover:text-slate-800"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-5 py-4 space-y-4">
          <div>
            <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">
              Root domain
            </label>
            <input
              type="text"
              value={rootDomain}
              onChange={(e) => setRootDomain(e.target.value)}
              disabled={isEdit}
              placeholder="wasabi.com"
              required
              className="w-full px-3 py-2 text-sm font-mono bg-cyber-100 border border-cyber-300/60 rounded-lg text-slate-900 placeholder-slate-500 outline-none focus:border-shinobi-green/70 disabled:opacity-50"
            />
            {isEdit && (
              <p className="text-[11px] text-slate-500 mt-1">Domain cannot be changed. Delete and recreate if needed.</p>
            )}
          </div>

          <div>
            <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">
              Friendly name (optional)
            </label>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder='e.g. "Wasabi primary"'
              className="w-full px-3 py-2 text-sm bg-cyber-100 border border-cyber-300/60 rounded-lg text-slate-900 placeholder-slate-500 outline-none focus:border-shinobi-green/70"
            />
          </div>

          <div>
            <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">
              Schedule
            </label>
            <select
              value={scheduleKind}
              onChange={(e) => setScheduleKind(e.target.value)}
              className="w-full px-3 py-2 text-sm bg-cyber-100 border border-cyber-300/60 rounded-lg text-slate-900 outline-none focus:border-shinobi-green/70"
            >
              {SCHEDULE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">
              Enumeration tools
            </div>
            <SubdomainToolPicker
              tools={tools}
              onToolsChange={setTools}
              wordlistSize={wordlistSize}
              onWordlistSizeChange={setWordlistSize}
            />
          </div>

          {error && (
            <div className="rounded-lg border border-red-300 bg-red-100 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-3 py-2 text-sm text-slate-700 hover:text-slate-900"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !rootDomain.trim()}
              className="px-4 py-2 text-sm rounded-lg bg-wasabi-100 text-wasabi-700 border border-wasabi-300 hover:bg-shinobi-green/25 disabled:opacity-50"
            >
              {submitting ? 'Saving...' : isEdit ? 'Save changes' : 'Create monitor'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
